import { useQuery } from '@apollo/client';
import { useLocalStorage } from '@uidotdev/usehooks';
import { useEffect, useRef } from 'react';
import { useMount, useUnmount } from 'react-use';

import { BannerToastType, FCC, bannerToast } from '@eluve/components';
import { SiteBannerTypesEnum, SiteBanners } from '@eluve/graphql-types';
import { useSession, useUserIdFromSession } from '@eluve/session-helpers';

import { SiteBannerMarkdownContent } from './SiteBannerMarkdownContent';
import { getSiteBannersQuery } from './operations';

const SITE_BANNER_LOCAL_STORAGE_KEY = 'dismissedSiteBanners';
const POLL_INTERVAL = 1 * 60 * 1000; // 1 minute in milliseconds

type BannerTypeMapping = Record<SiteBannerTypesEnum, BannerToastType>;

const BannerTypeToToastTypeMapping: BannerTypeMapping = {
  INFO: 'info',
  WARNING: 'warning',
  CRITICAL: 'error',
} as const;

const sanitizeLocalStorageArray = (key: string) => {
  const raw = localStorage.getItem(key);
  if (raw === null) return;
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      localStorage.removeItem(key);
    }
  } catch {
    localStorage.removeItem(key);
  }
};

export const SiteBannerProvider: FCC = ({ children }) => {
  const userId = useUserIdFromSession();
  const session = useSession();

  const shownNotificationsRef = useRef<Set<string>>(new Set());

  // Sanitize before useLocalStorage reads the value, since the hook
  // calls JSON.parse internally and will throw on corrupt data.
  const sanitizedRef = useRef(false);
  if (!sanitizedRef.current) {
    sanitizeLocalStorageArray(SITE_BANNER_LOCAL_STORAGE_KEY);
    sanitizedRef.current = true;
  }

  const [dismissedSiteBanners, setDismissedSiteBanners] = useLocalStorage<
    string[]
  >(SITE_BANNER_LOCAL_STORAGE_KEY, []);

  const handleDismiss = (id: string) => () => {
    setDismissedSiteBanners((prev) => [...(prev ?? []), id]);
  };

  const handleShowNotification = (item: SiteBanners) => {
    if (dismissedSiteBanners?.includes(item.id)) return;

    bannerToast[BannerTypeToToastTypeMapping[item.bannerType]](item.title, {
      description: <SiteBannerMarkdownContent message={item.message} />,
      onDismiss: handleDismiss(item.id),
    });
  };

  const handleShowNotificationRef = useRef(handleShowNotification);
  handleShowNotificationRef.current = handleShowNotification;

  const siteBanners = useQuery(getSiteBannersQuery, {
    skip: !session || !userId,
    notifyOnNetworkStatusChange: true,
  });

  useEffect(() => {
    const data = siteBanners.data;
    if (!data?.siteBanners?.length) return;

    const newNotifications = data.siteBanners.filter(
      (item) => !shownNotificationsRef.current.has(item.id),
    );

    newNotifications.forEach((item) => {
      shownNotificationsRef.current.add(item.id);
      handleShowNotificationRef.current(item as SiteBanners);
    });
  }, [siteBanners.data]);

  useEffect(() => {
    // Stop polling if there's no valid session
    if (!session || !userId) {
      siteBanners.stopPolling();
      return;
    }

    const handleVisibilityChange = () => {
      if (document.hidden) {
        siteBanners.stopPolling();
      } else {
        siteBanners.startPolling(POLL_INTERVAL);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup function will be called when session/userId changes or component unmounts
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [session, userId, siteBanners]);

  useMount(() => {
    // Workaround to get polling work with onCompleted.
    // Typically, onCompleted gets fired once, however,
    // here we set `notifyOnNetworkStatusChange` to true
    // to ensure onCompleted gets triggered.
    siteBanners.startPolling(POLL_INTERVAL);
  });

  useUnmount(() => {
    shownNotificationsRef.current.clear();
    siteBanners.stopPolling();
  });

  return children;
};
